import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Scaling, Database, RefreshCw } from 'lucide-react';

const OtherVendorsSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Google's scale-first, Microsoft's data-first, and Alibaba's self-improvement approaches"
      >
        Google, Microsoft & Alibaba Approaches
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Google */}
          <div className="slide-card border-accent/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
                <Scaling className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Google</h3>
                <span className="text-xs text-accent">Scale & Prompting</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-accent/10">
                <p className="text-xs font-semibold text-accent mb-1">Core Philosophy</p>
                <p className="text-sm text-muted-foreground">
                  Reasoning is an <strong className="text-foreground">emergent ability</strong> that unlocks at sufficient scale (>100B params).
                </p>
              </div>
              
              <div>
                <p className="text-xs font-semibold text-foreground mb-2">Methodology</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Few-shot prompting</strong></li>
                  <li>• Provide exemplar triples</li>
                  <li>• Unlock latent reasoning via prompts</li>
                </ul>
              </div>
              
              <div className="p-2 rounded bg-muted/50 text-xs text-muted-foreground">
                <strong>Key Finding:</strong> CoT dramatically alters scaling curves for symbolic/arithmetic tasks.
              </div>
            </div>
          </div>
          
          {/* Microsoft */}
          <div className="slide-card border-chart-4/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-chart-4/10 border border-chart-4/20 flex items-center justify-center">
                <Database className="w-5 h-5" style={{ color: 'hsl(var(--chart-4))' }} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Microsoft</h3>
                <span className="text-xs" style={{ color: 'hsl(var(--chart-4))' }}>Synthetic Data</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: 'hsla(45, 93%, 58%, 0.1)' }}>
                <p className="text-xs font-semibold mb-1" style={{ color: 'hsl(var(--chart-4))' }}>Core Philosophy</p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">High-quality data</strong> is paramount. Small models can reason with impeccable data.
                </p>
              </div>
              
              <div>
                <p className="text-xs font-semibold text-foreground mb-2">Phi-4 Methodology</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Strategic <strong>synthetic data</strong></li>
                  <li>• Student surpasses teacher</li>
                  <li>• STEM-focused QA capabilities</li>
                </ul>
              </div>
              
              <div className="p-2 rounded bg-muted/50 text-xs text-muted-foreground">
                <strong>Result:</strong> 14B model outperforms much larger models on reasoning.
              </div>
            </div>
          </div>
          
          {/* Alibaba/Qwen */}
          <div className="slide-card border-chart-5/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-chart-5/10 border border-chart-5/20 flex items-center justify-center">
                <RefreshCw className="w-5 h-5" style={{ color: 'hsl(var(--chart-5))' }} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Alibaba (Qwen)</h3>
                <span className="text-xs" style={{ color: 'hsl(var(--chart-5))' }}>Self-Improvement</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: 'hsla(340, 82%, 62%, 0.1)' }}>
                <p className="text-xs font-semibold mb-1" style={{ color: 'hsl(var(--chart-5))' }}>Core Philosophy</p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Self-improvement</strong> throughout the entire pipeline.
                </p>
              </div>
              
              <div>
                <p className="text-xs font-semibold text-foreground mb-2">Iterative Pipeline</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li><strong>Pre-train:</strong> Generate math data</li>
                  <li><strong>Post-train:</strong> RM guides SFT evolution</li>
                  <li><strong>Inference:</strong> RM guides sampling</li>
                </ul>
              </div>
              
              <div className="p-2 rounded bg-muted/50 text-xs text-muted-foreground">
                <strong>Insight:</strong> Model guides its own training data iteration.
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border">
          <p className="text-sm text-center text-muted-foreground">
            <strong className="text-foreground">Common Thread:</strong> All vendors recognize that reasoning requires more than just scaling parameters—the training recipe matters.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default OtherVendorsSlide;
